import type { ApiError } from "../api/client";
import type { PreviewView, ValueChangeView } from "../api/types";
import { useCharacter } from "../character";
import { text } from "../text";
import { MessageList } from "./MessageList";
import { Sheet } from "./Sheet";

/** Before and after of a changed value, in currency units when the value has a currency. */
export function useChangeText() {
  const { amount } = useCharacter();
  return (change: ValueChangeView, side: "before" | "after"): string => {
    const { currency } = change;
    const num = side === "before" ? change.beforeNumber : change.afterNumber;
    const raw = side === "before" ? change.before : change.after;
    return currency && num != null ? amount(currency, num) : (raw ?? "–");
  };
}

export function PreviewDialog(props: {
  title: string;
  preview: PreviewView | null;
  error: ApiError | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { preview } = props;
  const changeText = useChangeText();
  const { amount } = useCharacter();
  const problems = preview?.messages.filter((m) => m.message.severity !== "info" && !m.acknowledged) ?? [];
  return (
    <Sheet
      title={props.title}
      onClose={props.onCancel}
      footer={
        <>
          <button type="button" className="button secondary" onClick={props.onCancel}>
            Abbrechen
          </button>
          <button type="button" className="button primary" disabled={!preview || props.busy} onClick={props.onConfirm} data-testid="confirm">
            Übernehmen
          </button>
        </>
      }
    >
      {props.error && <p className="error">{props.error.message}</p>}
      {!preview && !props.error && <p className="muted">Auswirkung wird berechnet …</p>}
      {preview && (
        <>
          <p className="preview-summary">{text(preview.summary)}</p>
          {preview.changes.length === 0 ? (
            <p className="muted">Keine Werte ändern sich.</p>
          ) : (
            <table className="changes" aria-label="Vorher und nachher">
              <thead>
                <tr>
                  <th>Wert</th>
                  <th>vorher</th>
                  <th>nachher</th>
                </tr>
              </thead>
              <tbody>
                {preview.changes.map((change) => (
                  <tr key={change.key}>
                    <td>{text(change.label)}</td>
                    <td>{changeText(change, "before")}</td>
                    <td className={trend(change.beforeNumber, change.afterNumber)}>{changeText(change, "after")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {preview.bookings.length > 0 && (
            <ul className="bookings-preview">
              {preview.bookings.map((b, i) => (
                <li key={i}>
                  {amount(b.currency, b.amount, true)}: {b.reason}
                </li>
              ))}
            </ul>
          )}
          {problems.length > 0 && <MessageList messages={problems} compact />}
        </>
      )}
    </Sheet>
  );
}

function trend(before: number | null | undefined, after: number | null | undefined): string {
  if (before == null || after == null) return "";
  return after > before ? "up" : after < before ? "down" : "";
}
